import type {
  CarePlan,
  Coding,
  Composition,
  Patient,
  Practitioner,
  QuestionnaireResponse,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeableConcept,
  displayFromCodeableConcept,
  hasCodeableConcept,
  keyByCodeableConcept,
} from '#src/codeableConcept.ts'
import { CARE_PLAN_TAG_SYSTEM, CARE_PLAN_TYPE } from '#src/codeSystems.ts'
import { Tag } from '#src/decorators.ts'
import { BasePlan } from '#src/models/care-plans/BasePlan.ts'
import {
  CarePlanState,
  CLOSED_CARE_PLAN_STATUSES,
  TOP_LEVEL_CARE_PLAN_CATEGORY,
} from '#src/models/care-plans/constants.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

/**
 * Initial Care Plan
 *
 * Life cycle
 *
 * | State                  | Intent  | Status     | Content Editable |
 * |------------------------|---------|------------|------------------|
 * | Draft                  | plan    | draft      | Yes              |
 * | Pending Signatures     | plan    | on-hold    | Yes              |
 * | Signed-off             | order   | on-hold    | No               |
 * | Active                 | plan    | active     | Yes              |
 * | Closed                 | order   | completed  | No               |
 * | Voided                 | *       | revoked    | No               |
 *
 */

export const NO_PARTICIPANT_SUMMARY_TAG_CODE = 'noParticipantSummary'

export class InitialCarePlan extends BasePlan {
  @Tag({
    system: CARE_PLAN_TAG_SYSTEM,
  })
  _noParticipantSummary?: Coding

  constructor({
    subject,
    title,
    contributors,
    type = 'INITIAL',
    ...rest
  }: Omit<CarePlan, 'resourceType' | 'intent' | 'status' | 'contributor'> & {
    subject: CarePlan['subject']
    title?: string
    contributors?: Reference<Practitioner>[]
    type?: keyof typeof InitialCarePlan.TYPES
  }) {
    super({
      title: title ?? 'New Care Plan',
      subject,
      category: [TOP_LEVEL_CARE_PLAN_CATEGORY, InitialCarePlan.TYPES[type]],
      contributor: contributors,
      ...rest,
    })
  }

  get noParticipantSummary(): boolean {
    return this._noParticipantSummary?.code === NO_PARTICIPANT_SUMMARY_TAG_CODE
  }

  set noParticipantSummary(value: boolean) {
    this._noParticipantSummary = value
      ? {
          system: CARE_PLAN_TAG_SYSTEM,
          code: NO_PARTICIPANT_SUMMARY_TAG_CODE,
        }
      : undefined
  }

  static searchQuery(): QueryTypes {
    return {
      category: TOP_LEVEL_CARE_PLAN_CATEGORY,
      'status:not': 'entered-in-error',
      _count: 1000,
      _sort: 'date',
    }
  }

  static byParticipant(patientRef: Reference<Patient>): QueryTypes {
    return {
      ...InitialCarePlan.searchQuery(),
      subject: patientRef.reference,
    }
  }

  static queryOpenByParticipant(patientRef: Reference<Patient>): QueryTypes {
    return {
      ...InitialCarePlan.byParticipant(patientRef),
      'status:not': CLOSED_CARE_PLAN_STATUSES,
    }
  }

  static byState(state: CarePlanState): QueryTypes {
    return {
      ...InitialCarePlan.searchQuery(),
      ...InitialCarePlan.stateToOrderAndIntent(state),
    }
  }

  static TYPES = {
    INITIAL: codeableConcept({
      system: CARE_PLAN_TYPE,
      code: 'initial',
      display: 'Initial',
    }),
    SEMI_ANNUAL: codeableConcept({
      system: CARE_PLAN_TYPE,
      code: 'semi-annual',
      display: '180 Days',
    }),
    CHANGE_OF_CARE: codeableConcept({
      system: CARE_PLAN_TYPE,
      code: 'change-of-care',
      display: 'Change of Condition',
    }),
  }

  get type(): keyof typeof InitialCarePlan.TYPES {
    return keyByCodeableConcept(InitialCarePlan.TYPES, this.resource.category)
  }

  set type(type) {
    const category = InitialCarePlan.TYPES[type]
    this.resource.category = [
      ...(this.resource.category ?? []).filter((category) =>
        category.coding?.find((coding) => coding.system !== CARE_PLAN_TYPE)
      ),
      category,
    ]
  }

  get typeDisplay(): string {
    return (
      displayFromCodeableConcept(this.resource.category, CARE_PLAN_TYPE) ?? ''
    )
  }

  get isEditable(): boolean {
    return (
      this.state === CarePlanState.Draft ||
      this.state === CarePlanState.PendingSignatures ||
      this.state === CarePlanState.Active
    )
  }

  get isDraft(): boolean {
    return this.state === CarePlanState.Draft
  }

  get supportingQuestionnaireResponses(): Reference<QuestionnaireResponse>[] {
    return (this.resource.supportingInfo ?? []).filter((ref) =>
      isOfType(ref, 'QuestionnaireResponse')
    )
  }

  get signedSnapshots(): Reference<Composition>[] {
    return (this.resource.supportingInfo ?? []).filter((ref) =>
      isOfType(ref, 'Composition')
    )
  }

  set signedSnapshots(signedSnapshots: Reference<Composition>[]) {
    this.resource.supportingInfo = [
      ...(this.resource.supportingInfo ?? []).filter(
        (ref) => !isOfType(ref, 'Composition')
      ),
      ...signedSnapshots,
    ]
  }

  static isInstance(carePlan: BasePlan): carePlan is BasePlan {
    return hasCodeableConcept(carePlan.categories, TOP_LEVEL_CARE_PLAN_CATEGORY)
  }
}
